/**
 * Sentry for the web app: the record-os-web project.
 *
 * `main.tsx` calls `initSentry(router)` before the first render, so the page
 * load and every route change are spans named by their route.
 *
 * One trace from the click to the database: every call the app makes goes to
 * its own origin (`src/platform/fetch.ts`, through the Worker's proxy), and
 * Sentry adds `sentry-trace` and `baggage` to the calls that
 * `tracePropagationTargets` names. The server continues that trace
 * (apps/server/src/instrument.ts). No other host gets the headers.
 *
 * Off under the Vite dev server and the e2e suite (WEB_E2E=1), so only a
 * production build (`vite build`, the test-web deploy) sends events.
 *
 * A DSN only lets a client send events to this one project, so it is
 * committed, as every browser that loads the page receives it anyway.
 */
import * as Sentry from '@sentry/react';
import type { AnyRouter } from '@tanstack/react-router';

const DSN = 'https://bd92eb45f459adb52a240b3e362e8070@o200975.ingest.us.sentry.io/4512208475324417';

export const initSentry = (router: AnyRouter): void => {
  const environment = import.meta.env.MODE === 'production' ? 'production' : 'development';
  Sentry.init({
    dsn: DSN,
    enabled: environment === 'production' && process.env.EXPO_PUBLIC_E2E !== '1',
    environment,
    release: `record-os-web@${__APP_VERSION__}`,
    integrations: [Sentry.tanstackRouterBrowserTracingIntegration(router)],
    tracesSampleRate: environment === 'production' ? 0.2 : 1.0,
    tracePropagationTargets: [globalThis.location.origin, /^\/(?!\/)/],
    // The session cookie stays in the browser.
    dataCollection: { cookies: false, httpBodies: [] },
  });
};

/** For `createRoot`: React 19 reports a render error here, not to window.onerror. */
export const reactErrorHandler = Sentry.reactErrorHandler;

export const captureException = Sentry.captureException;
